import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';

export type ThemeMode = 'light' | 'dark';

interface ThemeState {
  theme: ThemeMode;
  initialized: boolean;
}

const THEME_STORAGE_KEY = 'drf-theme';
const DEFAULT_THEME: ThemeMode = 'light';

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

function getStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  const storageCandidate = globalThis.localStorage as Partial<Storage> | undefined;

  if (!storageCandidate || typeof storageCandidate.getItem !== 'function' || typeof storageCandidate.setItem !== 'function') {
    return null;
  }

  return {
    getItem: storageCandidate.getItem.bind(storageCandidate),
    setItem: storageCandidate.setItem.bind(storageCandidate),
  };
}

export const ThemeStore = signalStore(
  { providedIn: 'root' },
  withState<ThemeState>({
    theme: DEFAULT_THEME,
    initialized: false,
  }),
  withMethods((store, documentRef = inject(DOCUMENT)) => {
    const applyTheme = (theme: ThemeMode): void => {
      const root = documentRef.documentElement;
      root.classList.remove('theme-light', 'theme-dark');
      root.classList.add(`theme-${theme}`);
    };

    const persistTheme = (theme: ThemeMode): void => {
      const storage = getStorage();

      if (!storage) {
        return;
      }

      storage.setItem(THEME_STORAGE_KEY, theme);
    };

    const setThemeState = (theme: ThemeMode): void => {
      patchState(store, { theme });
      applyTheme(theme);
      persistTheme(theme);
    };

    return {
      init(): void {
        const storage = getStorage();
        const storedTheme = storage?.getItem(THEME_STORAGE_KEY) ?? null;
        const theme = isThemeMode(storedTheme) ? storedTheme : DEFAULT_THEME;

        patchState(store, {
          initialized: true,
          theme,
        });

        applyTheme(theme);
      },

      toggleTheme(): void {
        const nextTheme: ThemeMode = store.theme() === 'light' ? 'dark' : 'light';
        setThemeState(nextTheme);
      },

      setTheme(theme: ThemeMode): void {
        setThemeState(theme);
      },

      setThemeFromStream: rxMethod<ThemeMode>(
        pipe(
          tap(theme => {
            if (isThemeMode(theme)) {
              setThemeState(theme);
            }
          }),
        ),
      ),
    };
  }),
);
